import { Clock, ExternalLink } from "lucide-react";
import { categoryLabel, importanceLabel, importanceClass, formatDate } from "@/lib/content";

const Block = ({ label, children, testid }) => {
  if (!children) return null;
  return (
    <div className="border-t border-bz-line pt-8" data-testid={testid}>
      <span className="bz-label text-bz-petrol">{label}</span>
      <p className="mt-4 whitespace-pre-line text-lg leading-loose text-bz-slate">{children}</p>
    </div>
  );
};

export const ArticleView = ({ article }) => {
  if (!article) return null;
  return (
    <div data-testid="article-view">
      <div className="rounded-sm border border-bz-line bg-bz-ivory2 px-6 py-4" data-testid="article-editorial-mention">
        <p className="text-sm leading-relaxed text-bz-slate">
          BENZENATI distingue les informations vérifiées de l'analyse et de la mise en perspective proposées.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <span className="border border-bz-line px-3 py-1 text-[0.65rem] uppercase tracking-[0.12em] text-bz-slate">
          {categoryLabel(article.category)}
        </span>
        <span className={`border px-3 py-1 text-[0.65rem] uppercase tracking-[0.12em] ${importanceClass(article.importance)}`} data-testid="article-importance">
          {importanceLabel(article.importance)}
        </span>
        {article.reading_time ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-bz-slate">
            <Clock size={14} strokeWidth={1.5} /> {article.reading_time} min de lecture
          </span>
        ) : null}
      </div>

      <h1 className="mt-8 font-serif text-4xl leading-[1.08] tracking-tight text-bz-navy lg:text-5xl">
        {article.title}
      </h1>

      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-bz-slate/80">
        {article.published_at && <span data-testid="article-date-pub">Publié le {formatDate(article.published_at)}</span>}
        {article.updated_at && <span data-testid="article-date-upd">Mis à jour le {formatDate(article.updated_at)}</span>}
      </div>

      {article.summary && (
        <p className="mt-8 border-l-2 border-bz-amber pl-6 font-serif text-xl italic leading-snug text-bz-slate" data-testid="article-summary">
          {article.summary}
        </p>
      )}

      {article.image && (
        <div className="mt-10 aspect-[16/9] overflow-hidden">
          <img
            src={article.image || "/articles/placeholder.webp"}
            alt={article.image_alt || "Illustration éditoriale BENZENATI"}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <div className="mt-12 space-y-8">
        <Block label="Entreprises concernées" testid="article-companies">{article.companies_concerned}</Block>
        <Block label="Le fait vérifié" testid="article-verified">{article.verified_fact}</Block>
        <Block label="Ce qui change concrètement" testid="article-changes">{article.what_changes}</Block>
        <Block label="Risques" testid="article-risks">{article.risks}</Block>
        <Block label="Opportunités" testid="article-opportunities">{article.opportunities}</Block>
        <Block label="Points à vérifier" testid="article-points">{article.points_to_verify}</Block>

        {article.analysis && (
          <div className="border-l-2 border-bz-amber bg-bz-ivory2 p-8 lg:p-10" data-testid="article-analysis">
            <span className="bz-label text-bz-petrol">Analyse & mise en perspective BENZENATI</span>
            <p className="mt-4 whitespace-pre-line text-lg leading-loose text-bz-navy">{article.analysis}</p>
          </div>
        )}
      </div>

      {article.sources?.length > 0 && (
        <div className="mt-12 border-t border-bz-line pt-8" data-testid="article-sources">
          <span className="bz-label text-bz-petrol">Sources officielles</span>
          <ul className="mt-4 space-y-3">
            {article.sources.map((s, i) => (
              <li key={i}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`article-source-${i}`}
                  className="bz-link-underline inline-flex items-center gap-2 text-base text-bz-petrol"
                >
                  {s.label} <ExternalLink size={14} strokeWidth={1.5} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-12 border-t border-bz-line pt-8">
        <p className="text-sm leading-relaxed text-bz-slate/80">
          Ce contenu constitue un décryptage informatif et ne remplace pas l'avis d'un professionnel compétent.
        </p>
      </div>
    </div>
  );
};
